import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { IThunkRejectValue, RootState } from "../../../types";
import {
  AiNotebooksState,
  AiNotebook,
  AiNotebookEditor,
  AiNotebookFreamwork,
  AiRegionData,
  AiResourceFlavor,
} from "../../../types/ai";
import { getExtractErrors } from "./../../../apis";
import { CustomErrorToast } from "./../../../components/general/Toast";
import {
  getAiNotebooksApi,
  createAiNotebookApi,
  deleteAiNotebookApi,
  updateAiNotebookApi,
  getAiNotebookEditorsApi,
  getAiNotebookFreamworkApi,
  getAiRegionsDataApi,
  getAiResourceFlavorsApi,
  aiNotebookSynchronizationApi,
  stopAiNotebookApi,
  startAiNotebookApi,
} from "../../../apis/ai-notebooksAPI";

export const AiNotebooksRefreshStatus = [
  "DELETING",
  "RESTARTING",
  //"RUNNING"
  "STARTING",
  "STOPPING",
];

const initialState: AiNotebooksState = {
  notebooks: [],
  notebooksLoading: false,
  notebooksActionLoading: false, //for create, delete, rename , update
  selectedNotebook: null, //for rename

  labelEditor: false,

  // editors
  editors: [],
  editorsLoading: false,

  // freamworks
  freamworks: [],
  freamworksLoading: false,

  // regions data
  regionsData: [],
  regionsDataLoading: false,

  // flavors
  flavors: [],
  flavorsLoading: false,
};

export const getAiNotebooksAsync = createAsyncThunk<
  { notebooks: Array<AiNotebook> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-notebooks",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiNotebooksAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiNotebooksApi();
      const notebooks = response.data.Result;
      return fulfillWithValue({ notebooks });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const updateAiNotebookAsync = createAsyncThunk<
  any,
  { id: string; data: any },
  IThunkRejectValue
>("ai-notebooks/update", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateAiNotebookApi(id, data);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const createAiNotebookAsync = createAsyncThunk<
  true,
  { data: any },
  IThunkRejectValue
>("ai-notebooks/create", async ({ data }, { rejectWithValue }) => {
  try {
    await createAiNotebookApi(data);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const deleteAiNotebookAsync = createAsyncThunk<
  true,
  { notebookId: string },
  IThunkRejectValue
>("ai-notebooks/delete", async ({ notebookId }, { rejectWithValue }) => {
  try {
    await deleteAiNotebookApi(notebookId);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const stopAiNotebookAsync = createAsyncThunk<
  any,
  { id: string },
  IThunkRejectValue
>("ai-notebooks/stop", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await stopAiNotebookApi(id);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const startAiNotebookAsync = createAsyncThunk<
  any,
  { id: string },
  IThunkRejectValue
>("ai-notebooks/start", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await startAiNotebookApi(id);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const getAiNotebookEditorsAsync = createAsyncThunk<
  { editors: Array<AiNotebookEditor> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-notebooks/editors",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiNotebookEditorsAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiNotebookEditorsApi();
      const editors = response.data.Result;
      return fulfillWithValue({ editors });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const getAiNotebookFreamworksAsync = createAsyncThunk<
  { freamworks: Array<AiNotebookFreamwork> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-notebooks/freamworks",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiNotebookFreamworksAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiNotebookFreamworkApi();
      const freamworks = response.data.Result;
      return fulfillWithValue({ freamworks });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const getAiRegionsDataAsync = createAsyncThunk<
  { regionsData: Array<AiRegionData> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-lab/regions-data",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiRegionsDataAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiRegionsDataApi();
      const regionsData = response.data.Result;
      return fulfillWithValue({ regionsData });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const getAiResourceFlavorsAsync = createAsyncThunk<
  { flavors: Array<AiResourceFlavor> },
  { region: string; withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-lab/resource-flavors",
  async (
    { region, withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiResourceFlavorsAsync.pending(requestId, {
          withoutLoading,
          region,
        })
      );

      const response = await getAiResourceFlavorsApi(region);
      const flavors = response.data.Result;
      return fulfillWithValue({ flavors });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const aiNotebookSynchronizationAsync = createAsyncThunk<
  true,
  { id: string; data: any },
  IThunkRejectValue
>("ai-notebooks/datasync", async ({ id, data }, { rejectWithValue }) => {
  try {
    await aiNotebookSynchronizationApi(id, data);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const aiNotebooksSlice = createSlice({
  name: "ai-notebooks",
  initialState,
  reducers: {
    handleSelectNotebook: (state, action: PayloadAction<AiNotebook>) => {
      state.selectedNotebook = action.payload;
    },
    handleToggleAiNotebookLabelModal: (
      state,
      action: PayloadAction<{ notebook: AiNotebook | null; isOpen: boolean }>
    ) => {
      state.selectedNotebook = action.payload.notebook;
      state.labelEditor = action.payload.isOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAiNotebooksAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.notebooksLoading = true;
      })
      .addCase(getAiNotebooksAsync.fulfilled, (state, action) => {
        const { notebooks } = action.payload;
        state.notebooks = notebooks;
        state.notebooksLoading = false;
      })
      .addCase(getAiNotebooksAsync.rejected, (state, { payload }) => {
        state.notebooksLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(updateAiNotebookAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(updateAiNotebookAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(updateAiNotebookAsync.rejected, (state, { payload }) => {
        state.notebooksActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(createAiNotebookAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(createAiNotebookAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(createAiNotebookAsync.rejected, (state, { payload }) => {
        state.notebooksActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(deleteAiNotebookAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(deleteAiNotebookAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(deleteAiNotebookAsync.rejected, (state, { payload }) => {
        state.notebooksActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(stopAiNotebookAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(stopAiNotebookAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(stopAiNotebookAsync.rejected, (state, { payload }) => {
        state.notebooksActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(startAiNotebookAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(startAiNotebookAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(startAiNotebookAsync.rejected, (state, { payload }) => {
        state.notebooksActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    //editors
    builder
      .addCase(getAiNotebookEditorsAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.editorsLoading = true;
      })
      .addCase(getAiNotebookEditorsAsync.fulfilled, (state, action) => {
        const { editors } = action.payload;
        state.editors = editors;
        state.editorsLoading = false;
      })
      .addCase(getAiNotebookEditorsAsync.rejected, (state, { payload }) => {
        state.editorsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    //freamworks
    builder
      .addCase(getAiNotebookFreamworksAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.freamworksLoading = true;
      })
      .addCase(getAiNotebookFreamworksAsync.fulfilled, (state, action) => {
        const { freamworks } = action.payload;
        state.freamworks = freamworks;
        state.freamworksLoading = false;
      })
      .addCase(getAiNotebookFreamworksAsync.rejected, (state, { payload }) => {
        state.freamworksLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    //regions data
    builder
      .addCase(getAiRegionsDataAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.regionsDataLoading = true;
      })
      .addCase(getAiRegionsDataAsync.fulfilled, (state, action) => {
        const { regionsData } = action.payload;
        state.regionsData = regionsData;
        state.regionsDataLoading = false;
      })
      .addCase(getAiRegionsDataAsync.rejected, (state, { payload }) => {
        state.regionsDataLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    //resource flavors
    builder
      .addCase(getAiResourceFlavorsAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.flavorsLoading = true;
      })
      .addCase(getAiResourceFlavorsAsync.fulfilled, (state, action) => {
        const { flavors } = action.payload;
        state.flavors = flavors;
        state.flavorsLoading = false;
      })
      .addCase(getAiResourceFlavorsAsync.rejected, (state, { payload }) => {
        state.flavorsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });
    builder
      .addCase(aiNotebookSynchronizationAsync.pending, (state) => {
        state.notebooksActionLoading = true;
      })
      .addCase(aiNotebookSynchronizationAsync.fulfilled, (state) => {
        state.notebooksActionLoading = false;
      })
      .addCase(
        aiNotebookSynchronizationAsync.rejected,
        (state, { payload }) => {
          state.notebooksActionLoading = false;
          if (payload?.message)
            toast.error(() => CustomErrorToast(payload?.message));
        }
      );
  },
});

export const selectAiNotebooks = (state: RootState) =>
  state.aiNotebooks.notebooks;
export const selectAiNotebooksLoading = (state: RootState) =>
  state.aiNotebooks.notebooksLoading;

export const selectAiNotebooksActionLoading = (state: RootState) =>
  state.aiNotebooks.notebooksActionLoading;

export const selectSelectedAiNotebook = (state: RootState) =>
  state.aiNotebooks.selectedNotebook;

export const selectAiNotebookLabelEditor = (state: RootState) =>
  state.aiNotebooks.labelEditor;

//editors
export const selectAiNotebookEditors = (state: RootState) =>
  state.aiNotebooks.editors;
export const selectAiNotebookEditorsLoading = (state: RootState) =>
  state.aiNotebooks.editorsLoading;

//freamwork
export const selectAiNotebookFreamworks = (state: RootState) =>
  state.aiNotebooks.freamworks;
export const selectAiNotebookFreamworksLoading = (state: RootState) =>
  state.aiNotebooks.freamworksLoading;

//regions data
export const selectAiRegionsData = (state: RootState) =>
  state.aiNotebooks.regionsData;
export const selectAiRegionsDataLoading = (state: RootState) =>
  state.aiNotebooks.regionsDataLoading;

//resource flavors
export const selectAiResourceFlavors = (state: RootState) =>
  state.aiNotebooks.flavors;
export const selectAiResourceFlavorsLoading = (state: RootState) =>
  state.aiNotebooks.flavorsLoading;

export const { handleSelectNotebook, handleToggleAiNotebookLabelModal } =
  aiNotebooksSlice.actions;
export default aiNotebooksSlice.reducer;
